import { useTranslation } from 'react-i18next'

import { mergeClassName } from '../../../utils/mergeClassName'
import { type Quote } from '../../../modules/quotes'
import AlternativeDates from '../AlternativeDates'
import { useQuoteContext } from '../QuoteComponent.context'
import ChargesDetails from '../ChargesDetails'
import Constraints from '../Constraints'
import JobSummary from './JobSummary'

type ReactDiv = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

type JobProps = ReactDiv & {
  quote: Quote
  jobIndex: number
}

const Job: React.FC<JobProps> = ({
  quote,
  jobIndex,
  ...props
}) => {
  const { t } = useTranslation()
  const {
    multiJobs,
    nbJobs,
    showAlternativeDates = true,
    showSummary = true,
  } = useQuoteContext()

  const job = quote.jobs[jobIndex]
  if (!job) {
    return null
  }

  const hasAlternativeDateDifference = !!job.alternativeDates?.find(date => date.dateAvailable && date.subtotal.price !== job.subtotal.price)

  return (
    <div
      {...props}
      className={mergeClassName(
        'px-4 md:px-0',
        props.className,
      )}
    >
      <Constraints quote={quote} jobIndex={jobIndex} />

      <div className="flex flex-col-reverse gap-6 md:flex-row lg:gap-x-12">

        { (showSummary || showAlternativeDates) && (
          <div className="flex flex-col justify-between">

            { showSummary && (
              <div className="hidden md:block md:min-w-[350px]">
                <JobSummary quote={quote} jobIndex={jobIndex} />
              </div>
            ) }

            { showAlternativeDates && (
              <div className={mergeClassName(
                !hasAlternativeDateDifference && 'hidden md:block',
              )}
              >
                <h2 className="mb-6 font-body2 text-sm font-bold text-neutral-700">
                  { t('quotes.quote.comparePrices') }
                </h2>
                <div className="md:max-w-[415px]">

                  <AlternativeDates
                    quote={quote}
                    jobIndex={jobIndex}
                  />
                </div>
              </div>
            ) }
          </div>
        ) }

        <div className="grow md:min-w-[420px]">
          { showSummary && (
            <div className="mb-6 md:hidden">
              <JobSummary quote={quote} jobIndex={jobIndex} />
            </div>
          ) }

          <h2 className="mb-2 font-body2 text-sm font-bold text-neutral-700">
            { multiJobs
              ? t('quotes.quote.estimatedChargesMultiDays', { day: jobIndex + 1, nbDays: nbJobs })
              : t('quotes.quote.estimatedCharges') }
          </h2>
          <ChargesDetails quote={quote} jobIndex={jobIndex} />
        </div>
      </div>
    </div>
  )
}

export default Job
